import React, { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { withRouter, Link } from "react-router-dom";
import Select from "react-select";
import { CircularProgress } from "@material-ui/core";
import { WarningModal } from "../../components/Modal";
import Icon from "../../components/Icon";
import {
  REQNotificationList,
  SENDxNotification,
  DELxNotification,
  SETxNotificationStatus,
  CleanUpNotificationList
} from "./redux/actions";
import { SEND_STATUS_LABEL } from "./api";
import "./style.scss";

// Status filter — server-side (sent as sendStatus; "ALL" means the filter is off).
const STATUS_OPTIONS = [
  { value: "ALL", label: "All statuses" },
  { value: "DRAFT", label: "Draft" },
  { value: "PENDING", label: "Pending" },
  { value: "SENDING", label: "Sending" },
  { value: "SENT", label: "Sent" },
  { value: "PARTIAL", label: "Partially sent" },
  { value: "FAILED", label: "Failed" },
  { value: "CANCELLED", label: "Cancelled" }
];

// Content type filter — client-side (the list API has no content-type parameter).
const CONTENT_TYPE_OPTIONS = [
  { value: "", label: "All types" },
  { value: "GENERAL", label: "General" },
  { value: "PROMO", label: "Promo" },
  { value: "URGENT", label: "Urgent" }
];

// CTA (actionType) filter — client-side, same as the content-type one.
const CTA_FILTER_OPTIONS = [
  { value: "", label: "All CTA" },
  { value: "NONE", label: "None" },
  { value: "URL", label: "Link" },
  { value: "DEEPLINK", label: "In-app" },
  { value: "SURVEY", label: "Survey" }
];

// Source = whether the notification was composed by an operator (Manual) or raised automatically
// by a backend event (Automatic). Derived client-side from origin / eventType; no API field.
const SOURCE_FILTER_OPTIONS = [
  { value: "", label: "All sources" },
  { value: "MANUAL", label: "Manual" },
  { value: "AUTOMATIC", label: "Automatic" }
];

// True when a notification was raised by an event rather than composed by an operator.
const isAutomatic = notification =>
  notification.origin === "AUTOMATED" || Boolean(notification.eventType);

const PAGE_SIZE = 20;

const CONTENT_TYPE_LABEL = {
  GENERAL: "General",
  PROMO: "Promo",
  URGENT: "Urgent"
};

/*
 * CTA = the "Button in the message" (actionType) a notification carries. Shown as plain text —
 * a short label per kind, or a dash for NONE. The destination (actionLink) stays as a tooltip.
 * For a survey, the survey code (actionLink) is read to name the kind: "Survey - CSAT" / "- NPS".
 */
const CTA_LABEL = {
  URL: "Link",
  DEEPLINK: "In-app",
  SURVEY: "Survey"
};

const ctaText = notification => {
  const type = notification.actionType;
  if (!type || type === "NONE") {
    return "—";
  }
  if (type === "SURVEY") {
    const link = notification.actionLink || "";
    const kind = /csat/i.test(link) ? "CSAT" : /nps/i.test(link) ? "NPS" : "";
    return kind ? `Survey - ${kind}` : "Survey";
  }
  return CTA_LABEL[type] || type;
};

const CONFIRM_TITLE = {
  SEND: notification =>
    `Send "${notification.header}" to the ${notification.userScope} customer group now?`,
  DELETE: notification => `Delete "${notification.header}"?`,
  // A delivered campaign is hidden rather than deleted: the customers who already have it
  // keep it out of sight, and its Reach / View history stays.
  DEACTIVATE: notification =>
    `Hide "${notification.header}" from customers' inbox? (Reach / View totals are kept)`,
  ACTIVATE: notification => `Show "${notification.header}" in the app again?`,
  // A scheduled send that has not gone out yet: cancelling keeps the record but stops the send.
  CANCEL: notification =>
    `Cancel the scheduled send of "${notification.header}"? It stays in the list as cancelled and will not be sent.`,
  RESUME: notification =>
    `Resume the scheduled send of "${notification.header}"? It sends at its scheduled time (or now if that time has passed).`
};

const formatDate = value => {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  return isNaN(date.getTime()) ? "—" : date.toLocaleString("th-TH");
};

const formatCount = value => (value == null ? "—" : Number(value).toLocaleString());

/*
 * A campaign that has gone out can no longer be edited or deleted; it is history.
 *
 * The test is the send time, not the send status: a campaign whose pushes all failed
 * still reached every recipient's inbox and may already have been read. This mirrors
 * the backend guard, which refuses the edit either way.
 */
const isDelivered = notification => Boolean(notification.timeSent);

/*
 * A campaign that is queued to go out but has not been sent yet: scheduled (a Send date in
 * the future) or otherwise pending. These are the ones an operator can still cancel.
 */
const isScheduledPending = notification =>
  !isDelivered(notification) && notification.sendStatus === "PENDING";

/*
 * A scheduled send the operator has cancelled. Cancelling flips the campaign to INACTIVE,
 * which the scheduler never picks up (it only dispatches ACTIVE + PENDING rows), so the
 * record and its Send date stay while the send is stopped. Resuming turns it back to ACTIVE.
 */
const isCancelledSchedule = notification =>
  isScheduledPending(notification) && notification.status !== "ACTIVE";

/*
 * The send status an operator sees, folding the cancelled-schedule case (INACTIVE + PENDING)
 * into CANCELLED. Drives both the badge label and its colour class (s-draft, s-sent, …).
 */
const effectiveStatus = notification =>
  isCancelledSchedule(notification) ? "CANCELLED" : notification.sendStatus;

function NotificationList(props) {
  const {
    REQNotificationList,
    SENDxNotification,
    DELxNotification,
    SETxNotificationStatus,
    CleanUpNotificationList,
    REQStrNotificationList,
    NotificationList,
    Pagination,
    errorMessage
  } = props;


  const [oSearch, SETxSearch] = useState("");
  const [oSubmittedSearch, SETxSubmittedSearch] = useState("");
  const [oFilter, SETxFilter] = useState("ALL");
  const [oPage, SETxPage] = useState(1);
  const [oConfirm, SETxConfirm] = useState(null);
  // The row whose actions (⋮) menu is open, or null.
  const [oMenu, SETxMenu] = useState(null);
  // Client-side filters (the list API has no source / content-type / CTA / date parameters).
  const [oSource, SETxSource] = useState("");
  const [oContentType, SETxContentType] = useState("");
  const [oCta, SETxCta] = useState("");
  const [oDateFrom, SETxDateFrom] = useState("");
  const [oDateTo, SETxDateTo] = useState("");

  const filters = {
    search: oSubmittedSearch,
    sendStatus: oFilter,
    page: oPage,
    limit: PAGE_SIZE
  };

  useEffect(() => {
    REQNotificationList(filters);
    return () => {
      CleanUpNotificationList();
    };
  }, [oSubmittedSearch, oFilter, oPage]);

  // Close the row actions menu on an outside click or Escape.
  useEffect(() => {
    if (!oMenu) {
      return undefined;
    }
    const close = event => {
      if (!event.target.closest || !event.target.closest(".nl-menu-wrap")) {
        SETxMenu(null);
      }
    };
    const esc = event => {
      if (event.key === "Escape") {
        SETxMenu(null);
      }
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [oMenu]);

  const onSearch = event => {
    event.preventDefault();
    SETxPage(1);
    SETxSubmittedSearch(oSearch.trim());
  };

  // Reset every filter (search box, both dropdowns, and the date range) back to its default.
  const onClearFilters = () => {
    SETxSearch("");
    SETxSubmittedSearch("");
    SETxFilter("ALL");
    SETxSource("");
    SETxContentType("");
    SETxCta("");
    SETxDateFrom("");
    SETxDateTo("");
    SETxPage(1);
  };

  const onConfirm = () => {
    if (!oConfirm) {
      return;
    }
    if (oConfirm.action === "SEND") {
      SENDxNotification({ id: oConfirm.notification.id, filters });
    }
    if (oConfirm.action === "DELETE") {
      DELxNotification({ id: oConfirm.notification.id, filters });
    }
    if (oConfirm.action === "DEACTIVATE") {
      SETxNotificationStatus({ id: oConfirm.notification.id, status: "INACTIVE", filters });
    }
    if (oConfirm.action === "ACTIVATE") {
      SETxNotificationStatus({ id: oConfirm.notification.id, status: "ACTIVE", filters });
    }
    // Cancel / resume a scheduled send: the same status flip, kept apart so the confirm copy fits.
    if (oConfirm.action === "CANCEL") {
      SETxNotificationStatus({ id: oConfirm.notification.id, status: "INACTIVE", filters });
    }
    if (oConfirm.action === "RESUME") {
      SETxNotificationStatus({ id: oConfirm.notification.id, status: "ACTIVE", filters });
    }
    SETxConfirm(null);
  };

  // Open the confirm dialog for a row action and close the menu.
  const ask = (action, notification) => {
    SETxMenu(null);
    SETxConfirm({ action, notification });
  };

  const rows = NotificationList || [];

  // Content type and date range are applied here, on the current page, because the list API
  // filters only by status and search. Status still comes filtered from the backend.
  const visibleRows = rows.filter(notification => {
    if (oSource && (isAutomatic(notification) ? "AUTOMATIC" : "MANUAL") !== oSource) {
      return false;
    }
    if (oContentType && notification.contentType !== oContentType) {
      return false;
    }
    // A row with no action recorded is treated as NONE, so the "None" filter catches old rows.
    if (oCta && (notification.actionType || "NONE") !== oCta) {
      return false;
    }
    const day = String(notification.timeSent || notification.scheduledAt || "").slice(0, 10);
    if (oDateFrom && (!day || day < oDateFrom)) {
      return false;
    }
    if (oDateTo && (!day || day > oDateTo)) {
      return false;
    }
    return true;
  });

  const hasFilters =
    oSubmittedSearch || oFilter !== "ALL" || oSource || oContentType || oCta || oDateFrom || oDateTo;

  const totalPages = (Pagination && Pagination.totalPages) || 0;
  const total = (Pagination && Pagination.total) || 0;
  const rangeStart = total === 0 ? 0 : (oPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(oPage * PAGE_SIZE, total);

  const statusBadge = notification => {
    const status = effectiveStatus(notification);
    const label = SEND_STATUS_LABEL[status] || status;
    const className = `nl-status-badge s-${(status || "").toLowerCase()}`;
    // A delivered campaign's status opens its delivery log: who it reached and who it did not.
    if (isDelivered(notification)) {
      return (
        <Link
          className={className}
          title="View delivery log"
          to={`/Notification_DeliveryLog?id=${notification.id}`}
        >
          {label}
        </Link>
      );
    }
    return <span className={className}>{label}</span>;
  };

  return (
    <Fragment>
      {REQStrNotificationList ? (
        <div className="funds-list-progress">
          <CircularProgress />
          <p className="funds-list-progress-text">Loading notifications...</p>
        </div>
      ) : null}

      <div className="noti-list-container">
        <div className="noti-list-topbar">
          <div className="noti-list-titles">
            <h2 className="noti-list-heading">Notification List</h2>
            <p className="noti-list-subheading">
              Manage campaigns, experiments and automatic notifications.
            </p>
          </div>

          <div className="noti-list-actions">
            <Link className="noti-list-create" to="/PushNoti_Compose">
              <Icon name="plus" size={18} />
              Create new
            </Link>
          </div>
        </div>

        {errorMessage ? <div className="noti-list-error">{errorMessage}</div> : null}

        <div className="nl-filter-row">
          <div className="nl-filter-field nl-filter-field-wide">
            <span className="nl-filter-label">Search</span>
            <form className="nl-search" onSubmit={onSearch}>
              <Icon name="search" size={18} />
              <input
                type="search"
                placeholder="Search by campaign name, description…"
                value={oSearch}
                onChange={event => SETxSearch(event.target.value)}
              />
            </form>
          </div>
          <div className="nl-filter-field">
            <span className="nl-filter-label">Source</span>
            <Select
              className="basic-single"
              classNamePrefix="select"
              aria-label="Filter by source"
              isSearchable={false}
              options={SOURCE_FILTER_OPTIONS}
              value={SOURCE_FILTER_OPTIONS.find(option => option.value === oSource) || null}
              onChange={option => SETxSource(option ? option.value : "")}
            />
          </div>
          <div className="nl-filter-field">
            <span className="nl-filter-label">Type</span>
            <Select
              className="basic-single"
              classNamePrefix="select"
              aria-label="Filter by type"
              isSearchable={false}
              options={CONTENT_TYPE_OPTIONS}
              value={CONTENT_TYPE_OPTIONS.find(option => option.value === oContentType) || null}
              onChange={option => SETxContentType(option ? option.value : "")}
            />
          </div>
          <div className="nl-filter-field">
            <span className="nl-filter-label">CTA</span>
            <Select
              className="basic-single"
              classNamePrefix="select"
              aria-label="Filter by CTA"
              isSearchable={false}
              options={CTA_FILTER_OPTIONS}
              value={CTA_FILTER_OPTIONS.find(option => option.value === oCta) || null}
              onChange={option => SETxCta(option ? option.value : "")}
            />
          </div>
          <div className="nl-filter-field">
            <span className="nl-filter-label">Status</span>
            <Select
              className="basic-single"
              classNamePrefix="select"
              aria-label="Filter by status"
              isSearchable={false}
              options={STATUS_OPTIONS}
              value={STATUS_OPTIONS.find(option => option.value === oFilter) || null}
              onChange={option => {
                SETxPage(1);
                SETxFilter(option ? option.value : "ALL");
              }}
            />
          </div>
          <div className="nl-filter-field nl-filter-field-date">
            <span className="nl-filter-label">From</span>
            <input
              className="nl-date-input"
              type="date"
              aria-label="From date"
              value={oDateFrom}
              onChange={event => SETxDateFrom(event.target.value)}
            />
          </div>
          <div className="nl-filter-field nl-filter-field-date">
            <span className="nl-filter-label">To</span>
            <input
              className="nl-date-input"
              type="date"
              aria-label="To date"
              min={oDateFrom}
              value={oDateTo}
              onChange={event => SETxDateTo(event.target.value)}
            />
          </div>
          <div className="nl-filter-actions">
            <button type="button" className="nl-clear" onClick={onClearFilters} disabled={!hasFilters}>
              <Icon name="close" size={18} />
              Clear filters
            </button>
          </div>
        </div>

        <div className="nl-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Source</th>
                <th>Type</th>
                <th>CTA</th>
                <th>Notification</th>
                <th>Time sent</th>
                <th className="nl-num">Reach</th>
                <th className="nl-num">View</th>
                <th className="nl-num">Click</th>
                <th>Status</th>
                <th className="nl-actions-head">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(notification => {
                const delivered = isDelivered(notification);
                const scheduled = isScheduledPending(notification);
                const cancelled = isCancelledSchedule(notification);
                // Automatic (event-triggered) notifications carry an origin/eventType from the
                // backend; manual campaigns do not. Drives the Source column.
                const automatic = isAutomatic(notification);
                return (
                  <tr key={notification.id}>
                    <td className="nl-code" title={notification.code}>
                      {notification.code || "—"}
                    </td>
                    <td className="nl-source-cell">{automatic ? "Automatic" : "Manual"}</td>
                    <td className="nl-type-cell">
                      {CONTENT_TYPE_LABEL[notification.contentType] || notification.contentType}
                    </td>
                    <td className="nl-cta-cell" title={notification.actionLink || ""}>
                      {ctaText(notification)}
                    </td>
                    <td className="nl-noti-cell">
                      <Link className="nl-name-btn" to={`/PushNoti_Compose?id=${notification.id}`}>
                        {notification.header}
                      </Link>
                      {notification.content ? (
                        <div className="nl-noti-body">{notification.content}</div>
                      ) : null}
                      <div className="nl-sub">
                        {[
                          notification.eventType,
                          notification.name,
                          notification.notificationType,
                          notification.userScope
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </td>
                    <td className="nl-date">
                      {formatDate(notification.timeSent || notification.scheduledAt)}
                    </td>
                    <td className="nl-num">{formatCount(notification.reach)}</td>
                    <td className="nl-num">{formatCount(notification.view)}</td>
                    <td className="nl-num">{formatCount(notification.click)}</td>
                    <td>{statusBadge(notification)}</td>
                    <td className="nl-menu-cell">
                      <div className="nl-menu-wrap">
                        <button
                          type="button"
                          className="nl-kebab"
                          aria-label={`Actions for ${notification.header}`}
                          aria-haspopup="menu"
                          aria-expanded={oMenu === notification.id}
                          onClick={() => SETxMenu(m => (m === notification.id ? null : notification.id))}
                        >
                          ⋮
                        </button>
                        {oMenu === notification.id ? (
                          <div className="nl-row-menu" role="menu">
                            <Link
                              role="menuitem"
                              to={`/PushNoti_Compose?id=${notification.id}`}
                              onClick={() => SETxMenu(null)}
                            >
                              {delivered ? "View" : "Edit"}
                            </Link>

                            {delivered ? (
                              <button
                                type="button"
                                role="menuitem"
                                onClick={() =>
                                  ask(notification.status === "ACTIVE" ? "DEACTIVATE" : "ACTIVATE", notification)
                                }
                              >
                                {notification.status === "ACTIVE" ? "Hide from inbox" : "Show in app"}
                              </button>
                            ) : (
                              <Fragment>
                                {cancelled ? (
                                  <button type="button" role="menuitem" onClick={() => ask("RESUME", notification)}>
                                    Resume
                                  </button>
                                ) : scheduled ? (
                                  <button type="button" role="menuitem" onClick={() => ask("CANCEL", notification)}>
                                    Cancel
                                  </button>
                                ) : notification.status === "ACTIVE" ? (
                                  <button type="button" role="menuitem" onClick={() => ask("SEND", notification)}>
                                    Send now
                                  </button>
                                ) : null}
                                <button
                                  type="button"
                                  role="menuitem"
                                  className="nl-danger"
                                  onClick={() => ask("DELETE", notification)}
                                >
                                  Delete
                                </button>
                              </Fragment>
                            )}
                          </div>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {!REQStrNotificationList && visibleRows.length === 0 ? (
            <div className="nl-empty">
              <p className="nl-empty-title">
                {hasFilters ? "No results match your filters" : "No notifications yet"}
              </p>
              <p className="nl-empty-sub">
                {hasFilters ? "Try another search or clear the filters." : "Use “Create new” to compose one."}
              </p>
            </div>
          ) : null}
        </div>

        {total > 0 ? (
          <div className="nl-pager">
            <span className="nl-pager-info">
              {rangeStart}–{rangeEnd} of {total}
            </span>
            <div className="nl-pager-btns">
              <button
                type="button"
                className="nl-pager-btn"
                disabled={oPage <= 1}
                onClick={() => SETxPage(oPage - 1)}
              >
                ‹ Prev
              </button>
              <span className="nl-pager-page">
                {(Pagination && Pagination.page) || oPage} / {totalPages || 1}
              </span>
              <button
                type="button"
                className="nl-pager-btn"
                disabled={oPage >= totalPages}
                onClick={() => SETxPage(oPage + 1)}
              >
                Next ›
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <WarningModal
        show={oConfirm !== null}
        onHide={() => SETxConfirm(null)}
        onHandleGo={onConfirm}
        onHandleCancel={() => SETxConfirm(null)}
        type={"Disapprove"}
        status={"1"}
        title={oConfirm ? CONFIRM_TITLE[oConfirm.action](oConfirm.notification) : ""}
      />
    </Fragment>
  );
}

const mapStateToProps = state => ({
  REQStrNotificationList: state.notificationListReducer.REQStrNotificationList,
  NotificationList: state.notificationListReducer.NotificationList,
  Pagination: state.notificationListReducer.Pagination,
  errorMessage: state.notificationListReducer.errorMessage
});

const mapDispatchToProps = {
  REQNotificationList,
  SENDxNotification,
  DELxNotification,
  SETxNotificationStatus,
  CleanUpNotificationList
};

export default compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(NotificationList);
